import React from 'react';
import {
    mergeClasses,
    DrawerBody,
    DrawerHeader,
    DrawerHeaderTitle,
    InlineDrawer,
    Button,
} from '@fluentui/react-components';
import {
    Dismiss20Regular,
    PanelRightContractRegular,
    PanelRightExpandRegular,
} from '@fluentui/react-icons';
import type { PageService, HostedFilesService } from 'owa-app-host-services';
import { observer } from 'owa-mobx-react';
import { useComputedValue } from 'owa-react-hooks/lib/useComputed';
import { useAction } from 'owa-react-hooks/lib/useAction';
import { MAIN_BUTTON_ID } from 'owa-copilot-dab';
import {
    closeCopilotChatDrawer,
    isExpandCollapseButtonShown,
    isCopilotChatDrawerExpanded,
    toggleCopilotChatDrawerExpandCollapse,
    onCopilotChatDrawerReady,
    resizeCopilotChatDrawer,
    getCopilotChatDrawerTrueWidth,
    getShowBizChatInReadingPane,
    logCopilotChatDrawerStartingWidth,
    getBizChatWindowKey,
    getHeartbeatMemorySizesMb,
} from 'owa-copilot-chat-drawer-store';
import { ResizeHandle, ResizeHandleScenario, ResizeHandleDirection } from 'owa-resize-handle';
import { M365ChatContainer } from 'owa-sidepane-bizchat';
import { getCopilotChatDrawerStyles } from './getCopilotChatDrawerStyles';
import { CopilotAccountSwitcher } from 'owa-copilot-account-picker-dialog';
import {
    getCurrentAccount as getCurrentAccountFromPicker,
    resetCopilotAccountPickerDialogStoreMutator,
} from 'owa-copilot-account-picker-store';
import {
    container,
    denselessMargin,
    drawerBody,
    drawerHeader,
    headerTitle,
    copilotIconAndTitle,
    drawerContainer,
    ssrBackground,
} from './CopilotChatDrawer.scss';
import {
    closeCopilotChatDrawer as CopilotChatDrawerStrings_closeCopilotChatDrawer,
    collapseCopilotChatDrawer as CopilotChatDrawerStrings_collapseCopilotChatDrawer,
    expandCopilotChatDrawer as CopilotChatDrawerStrings_expandCopilotChatDrawer,
} from '../localization/CopilotChatDrawer.locstring.json';
import loc, { isCurrentCultureRightToLeft } from 'owa-localize';
import { isGlobalFeatureEnabled, isAccountFeatureEnabled } from 'owa-feature-flags';
import { trace } from 'owa-trace';
import type { IContextualChat } from 'owa-bizchat-types/lib/types';
import { hasQueryStringParameter } from 'owa-querystring';
import type { MailboxInfo } from 'owa-client-types';
import { useRegisterNavigationLoopContainer } from 'owa-accessibility-loop-nav/lib/hooks/useRegisterNavigationLoopContainer';
import { LazyTasksHub, LazyTasksHubToggleButton } from 'owa-copilot-chat-tasks-hub';
import { isActive } from 'owa-copilot-chat-tasks-hub/lib/selectors/isActive';

const dismissIcon = <Dismiss20Regular />;
const expandIcon = <PanelRightExpandRegular />;
const collapseIcon = <PanelRightContractRegular />;

export type CopilotChatDrawerProps = {
    mailboxInfo: MailboxInfo;
    targetWindow: Window; // The host window of current CopilotChat instance
    isVisible: boolean;
    dismissOverride?: () => void; // If provided, this function will be called when the user clicks the dismiss button
    hideExpandCollapseButton?: boolean; // If true, hide the expand/collapse button
    elementStyles?: Partial<
        Record<'drawerContainer' | 'drawerHeader' | 'inlineDrawer', React.CSSProperties>
    >; // If provided, these styles will be applied to the corresponding elements
    getChatContext?: () => Promise<IContextualChat>; // Function to get the chat context annotation
    overrideScenario?: string; // If provided, this scenario will be passed to the M365ChatContainer to override the default scenario
    getPageService?: (appId: string) => PageService; // Function to get the page service. Used when hosted inside Teams where we need to create the PageService differently(HostedPageService).
    getFilesService?: () => HostedFilesService; // If provided, this factory will be called to get the service used to proxy file operations to the root hub. Used when hosted inside Teams Calendar.
    rootHub?: string; // Used in Teams to specify the root hub for the chat. In Teams Calendar, bizchat is hosted as a nested app (Teams -> Calendar -> Bizchat). We need to pass in the root hub(Teams) to ensure correct behavior in scenarios that need this info, like getting customized Zero Query.
    isNAAChannelRecommended?: boolean; // If true, Nested App Auth channel will be recommended for BizChat
    topOrigin?: string;
    isDeeplyNestedAppAuthSupported?: boolean;
    addNAATrustedOrigins?: (appOrigins: string[]) => Promise<string>;
    disableAutoFocus?: boolean;
};

export const CopilotChatDrawer: React.FunctionComponent<CopilotChatDrawerProps> = observer(
    function CopilotChatDrawer({
        mailboxInfo,
        targetWindow,
        isVisible,
        dismissOverride,
        hideExpandCollapseButton,
        elementStyles,
        getChatContext,
        overrideScenario,
        getPageService,
        getFilesService,
        rootHub,
        isNAAChannelRecommended,
        topOrigin,
        isDeeplyNestedAppAuthSupported,
        addNAATrustedOrigins,
        disableAutoFocus,
    }) {
        const { copilotPane, overrideFuiIconFont } = getCopilotChatDrawerStyles();

        const showExpandCollapseButton = hideExpandCollapseButton
            ? false
            : isExpandCollapseButtonShown(targetWindow);
        const isCopilotChatDrawerInExpandedState = isCopilotChatDrawerExpanded(targetWindow);
        const isRTL = isCurrentCultureRightToLeft();
        const isDenselessEnabled = isGlobalFeatureEnabled('rp-denseless');
        const isAccountSwitcherEnabled = isGlobalFeatureEnabled(
            'acct-copilot-bizchat-account-switcher'
        );
        const isDevMailboxInfoEnabled = isGlobalFeatureEnabled('mon-copilot-dev-mailboxInfo');

        const drawerRef = React.useRef<HTMLDivElement>(null);
        useRegisterNavigationLoopContainer('CopilotChat', drawerRef);
        const showInReadingPane = getShowBizChatInReadingPane(targetWindow);

        const selectedMailboxInfo = useComputedValue(() => {
            return isAccountSwitcherEnabled
                ? getCurrentAccountFromPicker() ?? mailboxInfo
                : mailboxInfo;
        }, [isAccountSwitcherEnabled, mailboxInfo]);

        const isDevHighMemoryEnabled =
            isGlobalFeatureEnabled('mon-copilot-dev-highMemory') &&
            isAccountFeatureEnabled('mon-copilot-bizchat-ocdi', selectedMailboxInfo);

        const isSSREnabled =
            isAccountFeatureEnabled('mon-copilot-bizchat-ocdi', selectedMailboxInfo) &&
            isAccountFeatureEnabled('mon-copilot-bizchat-ssr', selectedMailboxInfo);

        const { workingSetSizeMb, commitSizeMb, isProcessShared } = getHeartbeatMemorySizesMb();

        useAction(onCopilotChatDrawerReady);

        const logStartingWidth = React.useCallback(
            () => logCopilotChatDrawerStartingWidth(targetWindow, selectedMailboxInfo),
            [targetWindow, selectedMailboxInfo]
        );
        useAction(logStartingWidth);

        const handleExpandCollapseClick = React.useCallback(() => {
            toggleCopilotChatDrawerExpandCollapse(
                targetWindow,
                !isCopilotChatDrawerInExpandedState,
                selectedMailboxInfo
            );
        }, [targetWindow, isCopilotChatDrawerInExpandedState, selectedMailboxInfo]);

        const handleCloseClick = React.useCallback(() => {
            if (isAccountSwitcherEnabled) {
                resetCopilotAccountPickerDialogStoreMutator();
            }
            closeCopilotChatDrawer(targetWindow, selectedMailboxInfo);
        }, [targetWindow, selectedMailboxInfo, isAccountSwitcherEnabled]);

        const handleCopilotChatResized = React.useCallback(
            (_rpDimension: number, copilotChatDrawerWidth: number) => {
                resizeCopilotChatDrawer(targetWindow, copilotChatDrawerWidth, selectedMailboxInfo);
            },
            [targetWindow, selectedMailboxInfo]
        );

        const drawerHeaderActions = React.useMemo(() => {
            const expandCollapseButtonLabel = isCopilotChatDrawerInExpandedState
                ? loc(CopilotChatDrawerStrings_collapseCopilotChatDrawer)
                : loc(CopilotChatDrawerStrings_expandCopilotChatDrawer);

            const expandCollapseButtonIcon = isCopilotChatDrawerInExpandedState
                ? isRTL
                    ? expandIcon
                    : collapseIcon
                : isRTL
                ? collapseIcon
                : expandIcon;

            const closeButtonLabel = loc(CopilotChatDrawerStrings_closeCopilotChatDrawer);

            return (
                <>
                    {showExpandCollapseButton && (
                        <Button
                            appearance="transparent"
                            aria-label={expandCollapseButtonLabel}
                            title={expandCollapseButtonLabel}
                            onClick={handleExpandCollapseClick}
                            icon={expandCollapseButtonIcon}
                            onKeyDown={onExpandCollapseKeyDown}
                        />
                    )}
                    <Button
                        appearance="transparent"
                        aria-label={closeButtonLabel}
                        title={closeButtonLabel}
                        icon={dismissIcon}
                        onClick={dismissOverride ?? handleCloseClick}
                    />
                </>
            );
        }, [
            isCopilotChatDrawerInExpandedState,
            isRTL,
            showExpandCollapseButton,
            handleExpandCollapseClick,
            handleCloseClick,
            dismissOverride,
        ]);

        // Compute the style to make width of the drawer component
        const copilotChatDrawerWidth = getCopilotChatDrawerTrueWidth(targetWindow);
        const drawerContainerStyle = useComputedValue(() => {
            trace.info(` Copilot chat drawer view state: isVisible: ${isVisible}`, 'iframeWrapper');
            if (showInReadingPane) {
                return {
                    display: isVisible ? 'flex' : 'none',
                    flexGrow: 1,
                    ...(elementStyles?.drawerContainer ?? {}),
                };
            }
            return {
                width: `${copilotChatDrawerWidth}px`,
                display: isVisible ? 'flex' : 'none',
                ...(elementStyles?.drawerContainer ?? {}),
            };
        }, [copilotChatDrawerWidth, isVisible, elementStyles?.drawerContainer, showInReadingPane]);

        const drawerHeaderClasses = mergeClasses(drawerHeader, overrideFuiIconFont);

        const isCachingEnabled = useComputedValue(() => {
            if (targetWindow && targetWindow !== window) {
                return false;
            }
            // Need to disable caching in dev mode because of a React Strict issue to not
            // disrupt dev process, will enable once fixed by MetaOS HubSDK team.
            if (isGlobalFeatureEnabled('mon-copilot-bizchat-caching')) {
                return process.env.NODE_ENV === 'dev'
                    ? hasQueryStringParameter('bizchatCaching')
                    : true;
            }
            return false;
        }, [targetWindow]);

        const windowKey = getBizChatWindowKey(targetWindow);

        const isAsyncChatEnabled = isGlobalFeatureEnabled('copilot-async-chat');
        const isTasksHubActive = isAsyncChatEnabled && isActive(windowKey);
        const isM365ChatVisible = isVisible && !isTasksHubActive;

        return (
            <>
                {isVisible && !showInReadingPane && (
                    <ResizeHandle
                        direction={ResizeHandleDirection.vertical}
                        onResized={handleCopilotChatResized}
                        scenario={ResizeHandleScenario.CopilotChatDrawer}
                        data-resize-scenario={'CopilotChatDrawer'}
                    />
                )}
                <div
                    data-app-section={'CopilotChat'}
                    ref={drawerRef}
                    className={mergeClasses(
                        container,
                        isDenselessEnabled && denselessMargin,
                        isSSREnabled && ssrBackground
                    )}
                    style={drawerContainerStyle}
                >
                    <InlineDrawer
                        position="end"
                        open={true}
                        className={drawerContainer}
                        style={elementStyles?.inlineDrawer}
                    >
                        <DrawerHeader className={drawerHeaderClasses}>
                            <DrawerHeaderTitle action={drawerHeaderActions}>
                                <div className={copilotIconAndTitle}>
                                    <span className={headerTitle}>Copilot</span>

                                    {isAsyncChatEnabled && (
                                        <LazyTasksHubToggleButton windowKey={windowKey} />
                                    )}
                                </div>
                            </DrawerHeaderTitle>
                        </DrawerHeader>

                        <DrawerBody className={drawerBody}>
                            <div className={copilotPane}>
                                {isDevMailboxInfoEnabled && (
                                    <div style={devMailboxInfoBannerStyle}>
                                        <div>[DEV] selectedMailboxInfo</div>
                                        <div>type: {selectedMailboxInfo.type}</div>
                                        <div>identity: {selectedMailboxInfo.userIdentity}</div>
                                        <div>smtp: {selectedMailboxInfo.mailboxSmtpAddress}</div>
                                        {selectedMailboxInfo.mailboxRank && (
                                            <div>rank: {selectedMailboxInfo.mailboxRank}</div>
                                        )}
                                    </div>
                                )}
                                {isDevHighMemoryEnabled &&
                                    renderDevHighMemoryBanner(
                                        workingSetSizeMb,
                                        commitSizeMb,
                                        isProcessShared
                                    )}
                                {isAccountSwitcherEnabled && (
                                    <CopilotAccountSwitcher
                                        currentContextMailboxInfo={selectedMailboxInfo}
                                        isDrawerVisible={isVisible}
                                        targetWindow={targetWindow}
                                    />
                                )}

                                {isTasksHubActive && <LazyTasksHub />}

                                <M365ChatContainer
                                    mailboxInfo={selectedMailboxInfo}
                                    isVisible={isM365ChatVisible}
                                    targetWindow={targetWindow}
                                    getChatContext={getChatContext}
                                    overrideScenario={overrideScenario}
                                    isCachingEnabled={isCachingEnabled}
                                    getPageService={getPageService}
                                    getFilesService={getFilesService}
                                    rootHub={rootHub}
                                    isNAAChannelRecommended={isNAAChannelRecommended}
                                    topOrigin={topOrigin}
                                    isDeeplyNestedAppAuthSupported={isDeeplyNestedAppAuthSupported}
                                    addNAATrustedOrigins={addNAATrustedOrigins}
                                    disableAutoFocus={disableAutoFocus}
                                />
                            </div>
                        </DrawerBody>
                    </InlineDrawer>
                </div>
            </>
        );
    },
    'CopilotChatDrawer'
);

// Accessibility implement to ensure shift+tab on expand/collapse properly
// goes back to the Copilot Command Center split action primary button.
const onExpandCollapseKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Tab' && e.shiftKey) {
        e.preventDefault();
        const target = document.getElementById(MAIN_BUTTON_ID);
        target?.focus();
    }
};

// This style is only used when dev flight is enabled to display the selected mailbox info for debugging purpose.
const devMailboxInfoBannerStyle: React.CSSProperties = {
    padding: '4px 8px',
    background: '#1a1a2e',
    color: '#00ff88',
    fontFamily: 'monospace',
    fontSize: '11px',
    borderBottom: '1px solid #333',
    userSelect: 'text',
};

// This style is only used when dev flight is enabled to display high memory warning.
const devHighMemoryBannerStyle: React.CSSProperties = {
    padding: '4px 8px',
    background: '#2e1a1a',
    color: '#ff4444',
    fontFamily: 'monospace',
    fontSize: '11px',
    borderBottom: '1px solid #333',
    userSelect: 'text',
};

function renderDevHighMemoryBanner(
    workingSetSizeMb: number | undefined,
    commitSizeMb: number | undefined,
    isProcessShared: boolean | undefined
): React.ReactNode {
    const workingSetThreshold = isProcessShared ? 550 : 300;
    const commitSizeThreshold = isProcessShared ? 650 : 350;

    const isOverThreshold =
        (workingSetSizeMb !== undefined && workingSetSizeMb > workingSetThreshold) ||
        (commitSizeMb !== undefined && commitSizeMb > commitSizeThreshold);

    if (!isOverThreshold) {
        return null;
    }

    return (
        <div style={devHighMemoryBannerStyle}>
            <div>⚠️ [DEV] High Memory Detected</div>
            <div>Working Set: {workingSetSizeMb ?? '?'} MB</div>
            <div>Commit Size: {commitSizeMb ?? '?'} MB</div>
            <div>
                Process Shared: {isProcessShared === undefined ? '?' : String(isProcessShared)}
            </div>
        </div>
    );
}
